import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { useT } from "./i18n";

const AuthCtx = createContext(null);
const RANK = { read_only: 0, developer: 1, admin: 2, owner: 3 };
const DEFAULT_BRAND = { product_name: "CARIO Mail", logo_text: "CARIO", logo_suffix: "Mail", support_email: "", status_url: "" };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [wsId, setWsIdState] = useState(localStorage.getItem("cario_ws"));
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const { syncLang } = useT();

  const applyUser = useCallback((u) => {
    setUser(u);
    if (u?.lang) syncLang(u.lang);
    if (u && u.workspaces?.length) {
      const keep = u.workspaces.find((w) => w.id === localStorage.getItem("cario_ws"));
      const id = keep ? keep.id : u.workspaces[0].id;
      localStorage.setItem("cario_ws", id);
      setWsIdState(id);
    }
  }, [syncLang]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
    } catch (_) {
      setUser(false);
    }
  }, [applyUser]);

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setBrand(data.brand)).catch((e) => console.warn("Could not load brand config; using defaults", e));
    // Skip /me while returning from Google OAuth; AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    load();
  }, [load]);

  const setWsId = (id) => { localStorage.setItem("cario_ws", id); setWsIdState(id); };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed; clearing the local session anyway", e); }
    localStorage.removeItem("cario_ws");
    setUser(false);
  };

  const value = useMemo(() => {
    const ws = user?.workspaces?.find((w) => w.id === wsId) || null;
    return {
      user, setUser: applyUser, reload: load, logout, wsId, setWsId, workspace: ws, brand,
      can: (min) => !!ws && RANK[ws.role] >= RANK[min],
    };
  }, [user, wsId, brand, applyUser, load]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
